import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { Header } from '@vms/Header';
import { IconFileDownload, IconPlus } from '@svg';
import Button from '@components/Button';
import { Table } from '@components/Table';
import { Route, RouteParams } from '@core/routes/Route';
import { TextInput } from '@components/Inputs';
import { replaceRouteParam } from '@core/utils/string';
import { useDownloadLiveSurveyReports, useExportLiveSurveyXls } from '@core/api/useSurveyFile';
import useTableQuickSearch from '@core/hooks/useTableQuickSearch';
import {
    ExtendedSurvey,
    useGetSurveyInitialData,
    useGetExtendedSurveys,
    useGetSurveys,
    Survey,
} from '@core/api/useSurveys';

import NewSurveyPopup from './components/NewSurveyPopup';
import CloseSurveyPopup from './components/CloseSurveyPopup';
import LaunchSurveyPopup from './components/LaunchSurveyPopup';
import SurveysTableConfig from './components/SurveysTableConfig';

const fakeExtendSurvey = (survey: Survey): ExtendedSurvey => ({
    ...survey,
    expressCount: 0,
    fullTestCount: 0,
    publicItemsCount: 0,
    totalItemsCount: 0,
    completedSampleSize: 0,
    completedSampleSizePercentage: 0,
    requestedSampleSize: 0,
    customSampleTypeId: null,
});

const Surveys = () => {
    const [isCloseSurveyPopupOpen, setIsCloseSurveyPopupOpen] = useState(false);
    const [isLaunchSurveyPopupOpen, setIsLaunchSurveyPopupOpen] = useState(false);
    const [isNewSurveyPopupOpen, setIsNewSurveyPopupOpen] = useState(false);
    const [selectedSurveyId, setSelectedSurveyId] = useState<number | undefined>(undefined);

    const navigate = useNavigate();

    const {
        data: surveysData,
        doFetch: fetchSurveys,
        isLoading: isSurveysLoading,
    } = useGetSurveys();

    const {
        data: surveysExtendedData,
        doFetch: fetchExtendedSurveys,
        isLoading: isExtendedSurveysLoading,
    } = useGetExtendedSurveys();

    const fetchSurveysData = useCallback(() => {
        fetchSurveys().catch(console.error);
        fetchExtendedSurveys().catch(console.error);
    }, [fetchSurveys, fetchExtendedSurveys]);

    const tableData: ExtendedSurvey[] = useMemo(() => {
        const surveysBasicData = isSurveysLoading ? [] : surveysData?.map(fakeExtendSurvey);
        const extendedSurveys = isExtendedSurveysLoading ? surveysBasicData : surveysExtendedData;
        return extendedSurveys ?? [];
    }, [isExtendedSurveysLoading, isSurveysLoading, surveysData, surveysExtendedData]);

    const {
        searchValue,
        setSearchValue,
        searchResults,
    } = useTableQuickSearch(tableData, (s) => s?.name);

    const {
        data: surveyInitialData,
    } = useGetSurveyInitialData();

    const {
        isLoading: isExportLoading,
        doFetchAndDownload
    } = useExportLiveSurveyXls();

    const {
        isLoading: isDownloadingLiveReports,
        doFetchAndDownload: doFetchAndDownloadLiveReports
    } = useDownloadLiveSurveyReports();

    const tableActions = useMemo(
        () => ({
            onEdit: (survey?: ExtendedSurvey) => {
                if (!survey?.id) return;

                const surveyDetailsUrl = replaceRouteParam(Route.SurveyDetails, RouteParams.Id, survey.id);
                navigate(surveyDetailsUrl);
            },
            onLaunch: (survey?: ExtendedSurvey) => {
                if (!survey?.id) return;

                setSelectedSurveyId(survey.id);
                setIsLaunchSurveyPopupOpen(true);
            },
            onClose: (survey?: ExtendedSurvey) => {
                if (!survey?.id) return;

                setSelectedSurveyId(survey.id);
                setIsCloseSurveyPopupOpen(true);
            },
        }),
        [navigate],
    );

    return (
        <div className="Surveys">
            <Header
                rightContent={
                    <div className="ChainItems__actions">
                        <Button modifiers={['h-tall']} isDisabled={isDownloadingLiveReports} onClick={doFetchAndDownloadLiveReports}>
                            {isDownloadingLiveReports ? (
                                <>
                                    <CircularProgress size={13} color="inherit" />
                                    Downloading Live Reports
                                </>
                            ) : (
                                <>
                                    <IconFileDownload />
                                    Download Live Reports
                                </>
                            )}
                        </Button>
                        <Button modifiers={['h-tall']} onClick={() => setIsNewSurveyPopupOpen(true)}>
                            <IconPlus />
                            New Survey
                        </Button>
                        <Button modifiers={['h-tall']} isDisabled={isExportLoading} onClick={doFetchAndDownload}>
                            {isExportLoading ? (
                                <>
                                    <CircularProgress size={13} color="inherit" />
                                    Export in progress
                                </>
                            ) : (
                                <>Live Survey Export</>
                            )}
                        </Button>
                        <TextInput value={searchValue} setValue={setSearchValue} placeholder="Search surveys" />
                    </div>
                }
            >
                Surveys
            </Header>

            {!!surveyInitialData && (
                <NewSurveyPopup
                    open={isNewSurveyPopupOpen}
                    handleClosePopup={() => setIsNewSurveyPopupOpen(false)}
                    customFielding={surveyInitialData?.customFielding}
                />
            )}

            <LaunchSurveyPopup
                open={isLaunchSurveyPopupOpen}
                handleClosePopup={() => setIsLaunchSurveyPopupOpen(false)}
                onLaunchSurveyCallback={fetchSurveysData}
                surveyId={selectedSurveyId}
            />

            <CloseSurveyPopup
                open={isCloseSurveyPopupOpen}
                handleClosePopup={() => setIsCloseSurveyPopupOpen(false)}
                onCloseSurveyCallback={fetchSurveysData}
                surveyId={selectedSurveyId}
            />

            <SurveysTableConfig
                data={searchResults}
                actions={tableActions}
                customFielding={surveyInitialData?.customFielding}
                isExtendedDataLoading={isExtendedSurveysLoading}
            >
                <Table rowsCount={searchResults.length} isLoading={isSurveysLoading} />
            </SurveysTableConfig>
        </div>
    );
};

export default Surveys;
